import { roundCmOutcome } from "utils";

import { SIDE_BY_SIDE_ROW_KEYS, type SideBySideRowKey } from "consts";

import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { SoilSightOutcomesByScenario } from "pages/app/scenario-analysis/utils/forecast";
import { ResilienceBandKey, cumulativeGapByYear, findBreakEven, getResilienceCardBand } from "pages/app/scenario-analysis/utils/keyInsightCards";
import { hasCompletedProjection } from "pages/app/scenario-analysis/utils/keyInsightsHelpers";
import { scenariosHaveIdenticalCropYears } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { cumulativeMargin, percentChange } from "pages/app/scenario-analysis/utils/scenarioComparison";
import { ScenarioResilienceByScenarioId } from "pages/app/scenario-analysis/utils/scenarioResilienceScore";
import { getReferenceScenario } from "pages/app/scenario-analysis/utils/scenarioValidation";

/** Whole signed percent, rounded away from zero, so -1.5% does not read as -1%. */
const roundWholePercent = (value: number): number => Math.sign(value) * Math.round(Math.abs(value));

export interface SideBySideMetrics {
  scenarioId: string;
  /** The reference column: every "vs. baseline" row is a dash on it. */
  isBaseline: boolean;
  /** Row 1 primary: contribution margin summed over the horizon; null until the scenario has outcomes. */
  cumulativeMargin: number | null;
  /** Row 1 secondary: the printed total divided by the horizon, so the two lines agree on screen. */
  averageAnnualMargin: number | null;
  /** Row 2 primary: the gain on the baseline as a whole signed percent; null when the baseline is ~zero. */
  gainPercent: number | null;
  /** Row 2 secondary: the same gain in the farm's currency per ha. */
  gain: number | null;
  /** Row 3: years spent behind the baseline. 0 is ahead from the start; null on the baseline and when still behind. */
  yearsBehind: number | null;
  /** Row 3: still behind the baseline in the final year, so no number of years can be named. */
  isStillBehind: boolean;
  /** Row 3's comparison value: "Not within N years" ranks last rather than dropping out of the marking. */
  yearsBehindRank: number | null;
  /** Row 4: largest cumulative shortfall against the baseline (<= 0); exactly 0 reads as "Never behind". */
  deepestDip: number | null;
  /** Row 5: the Key insights resilience score, to one decimal. */
  resilienceScore: number | null;
  /** Row 5 secondary: banded off the unrounded score, so a 7.96 cannot read "strong" here and "good" on the card. */
  resilienceBand: ResilienceBandKey | null;
}

export interface SideBySideTable {
  /** Same order as the cards and the chart, so the columns line up with both. */
  scenarios: ScenarioData[];
  baselineScenarioId: string | null;
  metricsByScenarioId: Record<string, SideBySideMetrics>;
  /** Scenario ids holding the best value in each row; every column tied on it is included. */
  bestScenarioIdsByRow: Record<SideBySideRowKey, string[]>;
}

/** Which value wins a row. Dips are negative, so the highest of them is the shallowest. */
const ROW_BEST_DIRECTION: Record<SideBySideRowKey, "highest" | "lowest"> = {
  totalMargin: "highest",
  extraVsBaseline: "highest",
  yearsBehind: "lowest",
  deepestDip: "highest",
  resilience: "highest",
};

/** The figure each row is judged on, which is the one it prints, so a mark cannot contradict the cell. */
const ROW_VALUE: Record<SideBySideRowKey, (metrics: SideBySideMetrics) => number | null> = {
  totalMargin: (metrics) => metrics.cumulativeMargin,
  extraVsBaseline: (metrics) => metrics.gainPercent,
  yearsBehind: (metrics) => metrics.yearsBehindRank,
  deepestDip: (metrics) => metrics.deepestDip,
  resilience: (metrics) => metrics.resilienceScore,
};

const buildMetrics = (
  scenario: ScenarioData,
  baseline: ScenarioData | null,
  outcomes: SoilSightOutcomesByScenario | null,
  projectionYears: number,
  resilienceScores: ScenarioResilienceByScenarioId
): SideBySideMetrics => {
  const isBaseline = baseline?.id === scenario.id;
  const scenarioRows = outcomes?.[scenario.id] ?? [];
  const baselineRows = baseline ? outcomes?.[baseline.id] ?? [] : [];
  const hasOutcomes = hasCompletedProjection(scenario.id, outcomes);
  /* A copy of the baseline has no gain, no dip and no year to name: each would be a zero reading as a
     real result, which is the case the comparison box refuses outright. */
  const isIdenticalToBaseline = !isBaseline && scenariosHaveIdenticalCropYears(baseline, scenario);
  // Both sides have to have reported: against an empty baseline every gap would read as the whole margin.
  const isComparable = hasOutcomes && !isBaseline && !isIdenticalToBaseline && hasCompletedProjection(baseline?.id ?? "", outcomes);

  /* Every figure is rounded the way Key insights rounds it (roundCmOutcome, currently nearest 10), and
     the gain and the percentage come off the same unrounded totals that card uses, so the two surfaces
     cannot disagree. */
  const rawTotal = hasOutcomes ? cumulativeMargin(scenarioRows, projectionYears) : null;
  const rawBaselineTotal = isComparable ? cumulativeMargin(baselineRows, projectionYears) : null;
  const total = rawTotal === null ? null : roundCmOutcome(rawTotal);
  const gap = rawTotal !== null && rawBaselineTotal !== null ? roundCmOutcome(rawTotal - rawBaselineTotal) : null;
  const gainPercent = rawTotal !== null && rawBaselineTotal !== null ? percentChange(rawTotal, rawBaselineTotal) : null;

  // A year counts only where both sides reported it, so no overlap means there is nothing to compare.
  const gaps = isComparable ? cumulativeGapByYear(scenarioRows, baselineRows, projectionYears) : [];
  const breakEven = gaps.length > 0 ? findBreakEven(gaps) : null;
  // Rounding decides "Never behind": a shortfall too small for roundCmOutcome to register is not one the farmer can act on.
  const dip = breakEven ? roundCmOutcome(breakEven.dip) : null;
  // The years row reads off the same rounded dip, so it cannot claim a shortfall the dip row calls "Never behind".
  const wasBehind = breakEven !== null && dip !== 0;
  const resilienceScore = resilienceScores[scenario.id]?.baseScore ?? null;

  return {
    scenarioId: scenario.id,
    isBaseline,
    cumulativeMargin: total,
    averageAnnualMargin: total === null || projectionYears <= 0 ? null : Math.round(total / projectionYears),
    gainPercent: gainPercent === null ? null : roundWholePercent(gainPercent),
    gain: gap,
    yearsBehind: breakEven === null ? null : !wasBehind ? 0 : breakEven.year === null ? null : breakEven.year - 1,
    isStillBehind: wasBehind && breakEven?.year === null,
    yearsBehindRank: breakEven === null ? null : !wasBehind ? 0 : breakEven.year === null ? Number.POSITIVE_INFINITY : breakEven.year - 1,
    deepestDip: dip,
    /* One decimal: the plans separate at the tenth, so 8.0 against 8.5 has to show. The band takes the
       unrounded score, since the tier edges are whole numbers and rounding across one would split the surfaces. */
    resilienceScore: resilienceScore === null ? null : Math.round(resilienceScore * 10) / 10,
    resilienceBand: resilienceScore === null ? null : getResilienceCardBand(resilienceScore),
  };
};

/** Scenario ids carrying a row's best value. A lone value and a tie both count; only a dash is left out. */
const bestScenarioIdsForRow = (metrics: SideBySideMetrics[], rowKey: SideBySideRowKey): string[] => {
  const candidates = metrics
    .map((entry) => ({ scenarioId: entry.scenarioId, value: ROW_VALUE[rowKey](entry) }))
    .filter((entry): entry is { scenarioId: string; value: number } => entry.value !== null);

  if (candidates.length === 0) return [];

  const values = candidates.map((entry) => entry.value);
  const best = ROW_BEST_DIRECTION[rowKey] === "lowest" ? Math.min(...values) : Math.max(...values);
  // Nobody catching up inside the horizon is not a win, so the row carries no mark at all.
  if (!Number.isFinite(best)) return [];

  return candidates.filter((entry) => entry.value === best).map((entry) => entry.scenarioId);
};

export interface BuildSideBySideTableInput {
  scenarios: ScenarioData[];
  outcomes: SoilSightOutcomesByScenario | null;
  projectionYears: number;
  resilienceScores: ScenarioResilienceByScenarioId;
}

/** Every figure the table shows, derived once so the columns, the marks and the caption cannot disagree. */
export const buildSideBySideTable = ({ scenarios, outcomes, projectionYears, resilienceScores }: BuildSideBySideTableInput): SideBySideTable => {
  const baseline = getReferenceScenario(scenarios) ?? null;
  const metrics = scenarios.map((scenario) => buildMetrics(scenario, baseline, outcomes, projectionYears, resilienceScores));

  return {
    scenarios,
    baselineScenarioId: baseline?.id ?? null,
    metricsByScenarioId: Object.fromEntries(metrics.map((entry) => [entry.scenarioId, entry])),
    bestScenarioIdsByRow: Object.fromEntries(SIDE_BY_SIDE_ROW_KEYS.map((rowKey) => [rowKey, bestScenarioIdsForRow(metrics, rowKey)])) as Record<
      SideBySideRowKey,
      string[]
    >,
  };
};
